import { useCallback } from "react";

import { useQuery } from "@tanstack/react-query";

import { getAdminPracticeCrops, getPracticeCrops } from "services";

import { getEnvironment, getFarmIdFromLocalStorage, injectNames, useTranslation } from "utils";

import { DEFAULT_CACHE_TIME, GET_PRACTICES_CROPS } from "consts";

import { PracticeCrop, WithInjectedName } from "models";

const PLACEHOLDER_IMAGE_URL = "https://klimpublic.s3.eu-central-1.amazonaws.com/footprinting-crop-images/small/placeholder.webp";

// TODO: make an endpoint for this
export const useGetPracticeCrop = (cropId?: string) => {
  const crops = useGetPracticeCrops({ enabled: true });
  return crops.data?.find((crop) => crop.id === cropId);
};

const env = getEnvironment();

export type PracticeCropWithDefaults = WithInjectedName<PracticeCrop> & {
  imageURL: string;
};

export const useGetPracticeCrops = ({
  enabled,
  regionId,
  isAdmin = env.admin,
  version,
}: {
  enabled?: boolean;
  regionId?: string;
  isAdmin?: boolean;
  version?: string;
}) => {
  // Not having { useSuspense: false } caused unintended UI behavior — like a drawer opening and closing on mount,
  // due to remounting triggered by Suspense fallback.
  // DO NOT REMOVE unless you're explicitly handling Suspense with fallback UIs.
  const { t } = useTranslation("practices", { useSuspense: false });

  // Translate in select, not queryFn, so the cached data stays language-neutral and names follow language and namespace loads.
  const withTranslatedNames = useCallback(
    (crops: PracticeCrop[]): Array<PracticeCropWithDefaults> =>
      injectNames(crops, t, "crops").map((crop) => ({ ...crop, imageURL: crop.imageURL || PLACEHOLDER_IMAGE_URL })),
    [t]
  );

  // The backend picks the crop parameters (harvestIsMandatory, isModelReady) from the current farm's program,
  // so a list fetched for another farm must not be reused
  const farmId = getFarmIdFromLocalStorage();

  return useQuery({
    queryKey: [GET_PRACTICES_CROPS, enabled, regionId, isAdmin, version, farmId],
    queryFn: () => (isAdmin ? getAdminPracticeCrops({ enabled, regionId, version }) : getPracticeCrops({ enabled, regionId, version })),
    select: withTranslatedNames,
    gcTime: DEFAULT_CACHE_TIME,
    staleTime: DEFAULT_CACHE_TIME,
  });
};
